import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../../../utils/Api";

const SIZE_GROUPS = {
    PSA: 1,
    BGS: 2,
    CGC: 3,
    OTHER_GRADING: 4,
    QUANTITY: 5,
    UNGRADED: 6,
};

const MAIN_GRADING_ALIASES = ["PSA", "BGS", "CGC"];

const getSizeLabel = (size) => size?.US || "";

const getNumericValue = (label) => {
    const matches = label.match(/\d+(\.\d+)?/g);

    if (!matches) {
        return null;
    }

    return Number(matches[matches.length - 1]);
};

const getAlias = (item, label) => {
    const alias = item?.alias?.trim();

    if (alias) {
        return alias.toUpperCase();
    }

    const matchedAlias = MAIN_GRADING_ALIASES.find((gradingAlias) =>
        label.toUpperCase().startsWith(gradingAlias)
    );

    if (matchedAlias) {
        return matchedAlias;
    }

    if (/items?/i.test(label)) {
        return "QUANTITY";
    }

    if (/ungraded|raw/i.test(label)) {
        return "RAW";
    }

    return "";
};

const getGroupRank = (alias, label) => {
    if (MAIN_GRADING_ALIASES.includes(alias)) {
        return SIZE_GROUPS[alias];
    }

    if (alias === "QUANTITY" || /items?/i.test(label)) {
        return SIZE_GROUPS.QUANTITY;
    }

    if (alias === "RAW" || /ungraded|raw/i.test(label)) {
        return SIZE_GROUPS.UNGRADED;
    }

    return SIZE_GROUPS.OTHER_GRADING;
};

const compareSizeItems = (firstItem, secondItem) => {
    if (firstItem.groupRank !== secondItem.groupRank) {
        return firstItem.groupRank - secondItem.groupRank;
    }

    if (firstItem.groupRank === SIZE_GROUPS.QUANTITY) {
        return (
            (firstItem.numericValue ?? Number.MAX_SAFE_INTEGER) -
            (secondItem.numericValue ?? Number.MAX_SAFE_INTEGER)
        );
    }

    if (firstItem.groupRank !== SIZE_GROUPS.UNGRADED) {
        const numericSort =
            (secondItem.numericValue ?? Number.MIN_SAFE_INTEGER) -
            (firstItem.numericValue ?? Number.MIN_SAFE_INTEGER);

        if (numericSort !== 0) {
            return numericSort;
        }
    }

    if (firstItem.groupRank === SIZE_GROUPS.OTHER_GRADING) {
        const aliasSort = firstItem.alias.localeCompare(secondItem.alias);

        if (aliasSort !== 0) {
            return aliasSort;
        }
    }

    const labelSort = firstItem.label.localeCompare(secondItem.label);

    if (labelSort !== 0) {
        return labelSort;
    }

    return firstItem.index - secondItem.index;
};

const normalizeSizeLists = (data = []) => {
    if (!Array.isArray(data)) {
        return [];
    }

    const uniqueSizes = new Map();

    data.forEach((item, index) => {
        const size = item?.size || item;
        const label = getSizeLabel(size);

        if (!label || uniqueSizes.has(label)) {
            return;
        }

        const alias = getAlias(item, label);

        uniqueSizes.set(label, {
            alias,
            groupRank: getGroupRank(alias, label),
            index,
            label,
            numericValue: getNumericValue(label),
            size,
        });
    });

    return Array.from(uniqueSizes.values())
        .sort(compareSizeItems)
        .map((item) => item.size);
};

const useFetchSizeLists = () => {
    const [isError, setIsError] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [sizes, setSizes] = useState([]);

    const usSizes = useMemo(() => {
        if (sizes && sizes.length > 0) {
            return sizes.map((size) => size.US);
        }

        return [];
    }, [sizes]);

    useEffect(() => {
        let isMounted = true;

        const fetch = async () => {
            setIsFetching(true);

            try {
                const response = await apiClient().get("/sizes", {
                    params: { category: "trading cards" },
                });

                if (!isMounted) {
                    return;
                }

                setIsError(false);
                setSizes(normalizeSizeLists(response.data.data));
            } catch (_err) {
                if (!isMounted) {
                    return;
                }

                setIsError(true);
                setSizes([]);
            } finally {
                if (isMounted) {
                    setIsFetching(false);
                }
            }
        };

        fetch();

        return () => {
            isMounted = false;
        };
    }, []);

    return {
        isError,
        isFetching,
        sizes,
        usSizes,
    };
};

export default useFetchSizeLists;
